import React, { Suspense, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Environment, ContactShadows, PerspectiveCamera, useGLTF } from '@react-three/drei';
import * as THREE from 'three';
import useConfigurator from '../state/useConfigurator';

// Fallback component for non-WebGL browsers
const FallbackViewer = () => {
  const { selectedColor } = useConfigurator();
  return (
    <div className="w-full h-full flex items-center justify-center bg-gradient-to-b from-gray-50 to-gray-100">
      <div className="text-center">
        <div 
          className="w-64 h-64 mx-auto mb-4 rounded-full"
          style={{ backgroundColor: selectedColor.hex }}
        />
        <p className="text-sm text-gray-600">WebGL not supported. Showing color preview.</p>
      </div>
    </div>
  );
};

/**
 * Map model part names to logical parts for color tinting
 * 
 * Usage: Update these arrays to match your shoe model's mesh names
 * Example: If your shoe has a mesh named "shoe_body", add it to the 'upper' array
 * 
 * @function mapModelParts
 * @param {string} meshName - The name of the mesh from the 3D model
 * @returns {string|null} - Returns the part category or null if sole
 */
const PART_MAPPING = {
  // Upper body - gets primary color
  upper: ['body', 'upper', 'mesh', 'fabric', 'shoe', 'main'],
  
  // Laces - gets primary color
  laces: ['lace', 'string', 'cord', 'shoelace'],
  
  // Heel tab - gets darker shade of primary color
  heel_tab: ['heel', 'tab', 'pull', 'back'],
  
  // Sole - stays original color (not tinted)
  sole: ['sole', 'bottom', 'outsole', 'rubber', 'midsole']
};

/**
 * Maps a mesh name to its logical part category
 * @param {string} meshName - Name of the mesh to categorize
 * @returns {string|null} - Part category or null for sole
 */
function mapModelParts(meshName) {
  const name = meshName.toLowerCase();
  
  for (const [part, keywords] of Object.entries(PART_MAPPING)) {
    if (keywords.some(keyword => name.includes(keyword))) {
      return part === 'sole' ? null : part; // null means don't tint
    }
  }
  
  // Default: tint everything except explicitly named soles
  return 'upper';
}

function ShoeModel({ color, soleColor, laceColor }) {
  const groupRef = useRef();
  const { isRotating } = useConfigurator();
  const [modelLoaded, setModelLoaded] = React.useState(false);
  
  useFrame((state, delta) => {
    if (isRotating && groupRef.current) {
      groupRef.current.rotation.y += delta * 0.5;
    }
  });
  
  // Try to load the GLB model
  React.useEffect(() => {
    const checkModel = async () => {
      try {
        const response = await fetch('/models/shoe.glb');
        if (response.ok && response.headers.get('content-type')?.includes('model')) {
          setModelLoaded(true);
        }
      } catch (e) {
        console.log('Using fallback geometric model');
      }
    };
    checkModel();
  }, []);
  
  if (modelLoaded) {
    return (
      <group ref={groupRef}>
        <ModelWithColor color={color} soleColor={soleColor} laceColor={laceColor} />
      </group>
    );
  }
  
  // Fallback geometric shoe
  return (
    <group ref={groupRef}>
      {/* Main shoe upper body */}
      <mesh position={[0, 0.1, 0]} rotation={[0, -Math.PI / 6, 0]} castShadow>
        <boxGeometry args={[2.2, 0.9, 3.8]} />
        <meshStandardMaterial 
          color={color}
          roughness={0.65}
          metalness={0.05}
        />
      </mesh>
      
      {/* Toe cap detail */}
      <mesh position={[0.3, 0.1, 1.8]} rotation={[0, -Math.PI / 6, 0]} castShadow>
        <boxGeometry args={[1.8, 0.85, 0.6]} />
        <meshStandardMaterial 
          color={color}
          roughness={0.7}
          metalness={0.05}
        />
      </mesh>
      
      {/* Shoe sole (bottom) - configurable */}
      <mesh position={[0, -0.55, 0]} rotation={[0, -Math.PI / 6, 0]} castShadow>
        <boxGeometry args={[2.4, 0.4, 4.0]} />
        <meshStandardMaterial 
          color={soleColor === 'black' ? '#1A1A1A' : '#F5F0E8'}
          roughness={0.9}
          metalness={0.0}
        />
      </mesh>
      
      {/* Midsole detail */}
      <mesh position={[0, -0.35, 0]} rotation={[0, -Math.PI / 6, 0]} castShadow>
        <boxGeometry args={[2.3, 0.15, 3.9]} />
        <meshStandardMaterial 
          color={soleColor === 'black' ? '#2A2A2A' : '#FFFFFF'}
          roughness={0.8}
          metalness={0.0}
        />
      </mesh>
      
      {/* Heel tab (back) - darker shade */}
      <mesh position={[-0.15, 0.6, -1.9]} rotation={[0.1, -Math.PI / 6, 0]} castShadow>
        <boxGeometry args={[0.9, 0.7, 0.25]} />
        <meshStandardMaterial 
          color={new THREE.Color(color).multiplyScalar(0.75)}
          roughness={0.6}
          metalness={0.1}
        />
      </mesh>
      
      {/* Tongue */}
      <mesh position={[0.3, 0.5, 1.2]} rotation={[-0.3, -Math.PI / 6, 0]} castShadow>
        <boxGeometry args={[1.0, 0.8, 0.2]} />
        <meshStandardMaterial 
          color={new THREE.Color(color).multiplyScalar(0.95)}
          roughness={0.7}
          metalness={0.05}
        />
      </mesh>
      
      {/* Side accent panel */}
      <mesh position={[-1.0, 0, 0.3]} rotation={[0, -Math.PI / 6, 0]} castShadow>
        <boxGeometry args={[0.15, 0.8, 2.5]} />
        <meshStandardMaterial 
          color={new THREE.Color(color).multiplyScalar(0.85)}
          roughness={0.65}
          metalness={0.08}
        />
      </mesh>
    </group>
  );
}

function ModelWithColor({ color, soleColor, laceColor }) {
  const { scene } = useGLTF('/models/shoe.glb');
  
  React.useEffect(() => {
    if (scene) {
      // DEBUG MODE: Apply highly visible test colors to identify each mesh
      scene.traverse((child) => {
        if (child.isMesh) {
          const meshName = child.name.toLowerCase();
          
          // Clone material for color changes
          if (!child.userData.originalMaterial) {
            child.userData.originalMaterial = child.material.clone();
          }
          child.material = child.material.clone();
          
          // Apply colors based on user selections
          // BODY COLOR - should affect main shoe body
          // SOLE COLOR - should affect bottom sole
          // LACE COLOR - should affect laces
          
          // Strategy: Let's apply body color to ALL meshes EXCEPT sole and laces
          let finalColor;
          
          // FINAL CORRECTED MAPPING based on user feedback:
          // Color swatches are changing laces, lace buttons are changing body
          // This means we need to swap the assignments!
          
          // Identify sole meshes (shoe_4 and shoe_5 - this is CORRECT)
          if (meshName === 'shoe_4' || meshName === 'shoe_5') {
            // These are SOLE parts
            finalColor = soleColor === 'black' ? '#1A1A1A' : '#F5F0E8';
            console.log(`SOLE: ${child.name} -> ${soleColor}`);
          } 
          // Identify BODY meshes (shoe_1 and shoe_2 are the actual BODY, not laces!)
          else if (meshName === 'shoe_1' || meshName === 'shoe_2') {
            // These are BODY parts (was incorrectly set as laces)
            finalColor = color;
            console.log(`BODY: ${child.name} -> body color`);
          }
          // Everything else is LACES (shoe, shoe_3, shoe_6, shoe_7 are laces!)
          else {
            // These are LACE parts
            if (laceColor === 'same') {
              finalColor = color;
            } else if (laceColor === 'white') {
              finalColor = '#F8F8F8';
            } else {
              finalColor = '#1A1A1A';
            }
            console.log(`LACE: ${child.name} -> ${laceColor}`);
          }
          
          child.material.color = new THREE.Color(finalColor);
          
          // Enhance color for textured materials
          if (child.material.map) {
            child.material.color.multiplyScalar(1.5);
          }
          
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });
    }
  }, [scene, color, soleColor, laceColor]);
  
  return <primitive object={scene} scale={2.5} position={[0, -0.5, 0]} />;
}

const Viewer = () => {
  const { selectedColor, selectedSole, selectedLace } = useConfigurator();
  const [webGLSupported, setWebGLSupported] = React.useState(true);
  
  React.useEffect(() => {
    // Check WebGL support
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    setWebGLSupported(!!gl);
  }, []);
  
  if (!webGLSupported) {
    return <FallbackViewer />;
  }
  
  return (
    <div className=" -translate-y-24 w-full aspect-square relative bg-transparent overflow-hidden">
      <Canvas
        shadows
        camera={{ position: [0, 1.5, 8], fov: 50 }}
        gl={{ antialias: true, alpha: true }}
      >
        <PerspectiveCamera makeDefault position={[0, 1.5, 8]} />
        
        {/* Lighting setup - soft daylight */}
        <ambientLight intensity={0.6} />
        <directionalLight 
          position={[5, 5, 5]} 
          intensity={0.8} 
          castShadow
          shadow-mapSize-width={1024}
          shadow-mapSize-height={1024}
        />
        <directionalLight position={[-5, 3, -5]} intensity={0.3} />
        
        {/* Environment for realistic reflections */}
        <Environment preset="studio" />
        
        <Suspense fallback={null}>
          <ShoeModel 
            color={selectedColor.hex} 
            soleColor={selectedSole}
            laceColor={selectedLace}
          />
          
          {/* Contact shadow under shoe */}
          <ContactShadows
            position={[0, -0.75, 0]}
            opacity={0.3}
            scale={10}
            blur={2}
            far={4}
          />
        </Suspense>
        
        {/* Orbit controls - horizontal only */}
        <OrbitControls
          enableZoom={true}
          enablePan={false}
          minDistance={5}
          maxDistance={12}
          minPolarAngle={Math.PI / 3}
          maxPolarAngle={Math.PI / 2}
          autoRotate={false}
        />
      </Canvas>
    </div>
  );
};

export default Viewer;
export { mapModelParts, PART_MAPPING };
